'use client'
import type { ReactNode } from 'react'
import {
  Input,
  type InputProps,
  InputGroup,
  InputLeftElement,
  InputRightElement
} from '@chakra-ui/react'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import {
  FormDescription,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  FormMessage
} from '../FormField'

interface GrandFormInputFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends Omit<InputProps, 'name'> {
  name: TName
  label?: ReactNode
  description?: ReactNode
  tooltip?: ReactNode
  placeholder?: string
  type?: string
  required?: boolean
  optional?: boolean
  disabled?: boolean
  startElement?: ReactNode
  endElement?: ReactNode
  rules?: RegisterOptions<TFieldValues, TName>
  control?: Control<TFieldValues>
}

export const GrandFormInputField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label,
  description,
  tooltip,
  placeholder,
  type = 'text',
  required,
  optional,
  disabled,
  startElement,
  endElement,
  rules,
  control,
  ...inputProps
}: GrandFormInputFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      optional={optional}
      required={required}
      rules={rules}
      render={({ field }) => (
        <FormFieldControl disabled={disabled}>
          {label ? <FormFieldLabel tooltip={tooltip}>{label}</FormFieldLabel> : null}

          {startElement || endElement ? (
            <InputGroup w="100%">
              {startElement ? <InputLeftElement>{startElement}</InputLeftElement> : null}
              <Input
                {...field}
                disabled={disabled}
                placeholder={placeholder}
                type={type}
                value={field.value ?? ''}
                {...inputProps}
              />
              {endElement ? <InputRightElement>{endElement}</InputRightElement> : null}
            </InputGroup>
          ) : (
            <Input
              {...field}
              disabled={disabled}
              placeholder={placeholder}
              type={type}
              value={field.value ?? ''}
              {...inputProps}
            />
          )}

          {description ? <FormDescription>{description}</FormDescription> : null}

          <FormMessage />
        </FormFieldControl>
      )}
    />
  )
}
